<template>
  <section class="project-management">
    <template v-if="view === 'list'">
      <header class="page-heading">
        <div>
          <span>PROJECTS</span>
          <h2>项目管理</h2>
          <p>按客户归档项目，一个客户可以对应多个空间项目。</p>
        </div>
        <button class="primary-button" @click="openProjectDialog">
          <i class="el-icon-plus"></i>
          新建项目
        </button>
      </header>

      <div class="project-overview">
        <div><strong>{{ activeProjectCount }}</strong><span>进行中</span></div>
        <div><strong>{{ pendingProjectCount }}</strong><span>待推进</span></div>
        <div><strong>{{ completedProjectCount }}</strong><span>已完成</span></div>
      </div>

      <div class="project-toolbar">
        <label>
          <i class="el-icon-search"></i>
          <input v-model.trim="keyword" type="search" placeholder="搜索客户名称或项目名称" />
          <button v-if="keyword" title="清空搜索" @click="keyword = ''">
            <i class="el-icon-close"></i>
          </button>
        </label>
        <div role="tablist" aria-label="项目状态筛选">
          <button
            v-for="filter in filters"
            :key="filter.value"
            :class="{ active: activeFilter === filter.value }"
            @click="activeFilter = filter.value"
          >
            {{ filter.label }}
          </button>
        </div>
      </div>

      <div v-if="filteredClients.length" class="client-groups">
        <section v-for="client in filteredClients" :key="client.id" class="client-group">
          <header class="client-heading">
            <span class="client-avatar" :style="{ background: client.color }">
              {{ client.name.slice(0, 1) }}
            </span>
            <div>
              <h3>{{ client.name }}</h3>
              <p>{{ client.phone }} · {{ client.source }}</p>
            </div>
            <span>{{ visibleProjects(client).length }} 个项目</span>
          </header>

          <div class="project-list">
            <button
              v-for="project in visibleProjects(client)"
              :key="project.id"
              class="project-row"
              @click="openProject(client, project)"
            >
              <img :src="$ossImageUrl(project.cover, 'thumbnail')" :alt="project.name" loading="lazy" decoding="async" />
              <span class="project-main">
                <span class="project-title-line">
                  <strong>{{ projectIdentifier(client, project) }}</strong>
                  <em :class="`status-${project.status}`">{{ statusText(project.status) }}</em>
                </span>
                <small class="project-location">
                  <i class="el-icon-location-outline"></i>
                  {{ project.unit || project.name }}
                </small>
                <small>{{ project.type }} · {{ project.style }}</small>
              </span>
              <span class="project-stage">
                <small>当前阶段</small>
                <strong>{{ stageName(project.stageIndex) }}</strong>
              </span>
              <span class="project-next">
                <small>下一步</small>
                <strong>{{ project.nextAction }}</strong>
              </span>
              <i class="el-icon-arrow-right"></i>
            </button>
          </div>
        </section>
      </div>

      <div v-else class="empty-state">
        <i class="el-icon-folder-opened"></i>
        <strong>没有找到匹配项目</strong>
        <button @click="resetFilters">查看全部项目</button>
      </div>
    </template>

    <template v-else-if="activeProject && activeClient">
      <button class="back-button" @click="backToList">
        <i class="el-icon-back"></i>
        返回项目列表
      </button>

      <header class="detail-heading">
        <div>
          <span>{{ activeProject.code }}</span>
          <h2>{{ projectIdentifier(activeClient, activeProject) }}</h2>
          <p>{{ activeProject.name }} · {{ activeProject.type }} · {{ activeProject.style }}</p>
        </div>
        <div>
          <button class="secondary-button" @click="$emit('start-design', activeProject)">
            <i class="el-icon-magic-stick"></i>
            继续设计
          </button>
          <button class="primary-button" @click="advanceStage">
            推进阶段
            <i class="el-icon-right"></i>
          </button>
        </div>
      </header>

      <div class="detail-layout">
        <main class="detail-main">
          <section class="project-cover">
            <img :src="$ossImageUrl(activeProject.cover, 'preview')" :alt="activeProject.name" decoding="async" />
            <div>
              <span>设计方向</span>
              <h3>{{ activeProject.style }}</h3>
              <p>{{ activeProject.requirement }}</p>
            </div>
          </section>

          <section class="detail-section">
            <div class="section-heading">
              <div>
                <strong>项目进度</strong>
                <span>当前处于“{{ stageName(activeProject.stageIndex) }}”</span>
              </div>
              <b>{{ Math.round(((activeProject.stageIndex + 1) / stages.length) * 100) }}%</b>
            </div>
            <div class="stage-track">
              <div
                v-for="(stage, index) in stages"
                :key="stage"
                :class="{
                  completed: index < activeProject.stageIndex,
                  current: index === activeProject.stageIndex
                }"
              >
                <span><i v-if="index < activeProject.stageIndex" class="el-icon-check"></i></span>
                <small>{{ stage }}</small>
              </div>
            </div>
          </section>

          <section class="detail-section">
            <div class="section-heading">
              <div>
                <strong>近期方案</strong>
                <span>集中查看当前项目的效果图和参考方向</span>
              </div>
              <button @click="$emit('start-design', activeProject)">继续创作</button>
            </div>
            <div class="scheme-grid">
              <button
                v-for="(image, index) in activeProject.images"
                :key="image"
                @click="$emit('preview-image', image, activeProject)"
              >
                <img :src="$ossImageUrl(image, 'thumbnail')" :alt="`${activeProject.name}方案${index + 1}`" loading="lazy" decoding="async" />
                <span>方案 {{ String(index + 1).padStart(2, '0') }}</span>
              </button>
            </div>
          </section>

          <section class="detail-section next-step-section">
            <div class="section-heading">
              <div>
                <strong>接下来要做</strong>
                <span>只保留当前最重要的几件事</span>
              </div>
            </div>
            <label v-for="task in activeProject.tasks" :key="task.id" :class="{ done: task.done }">
              <input v-model="task.done" type="checkbox" />
              <span><i class="el-icon-check"></i></span>
              <strong>{{ task.title }}</strong>
              <small>{{ task.date }}</small>
            </label>
          </section>
        </main>

        <aside class="detail-aside">
          <section>
            <span>客户信息</span>
            <div class="client-card">
              <span class="client-avatar large" :style="{ background: activeClient.color }">
                {{ activeClient.name.slice(0, 1) }}
              </span>
              <div>
                <strong>{{ activeClient.name }}</strong>
                <small>{{ activeClient.phone }}</small>
              </div>
            </div>
            <p>{{ activeClient.source }}</p>
          </section>

          <section>
            <span>项目资料</span>
            <dl>
              <div><dt>项目类型</dt><dd>{{ activeProject.type }}</dd></div>
              <div><dt>设计风格</dt><dd>{{ activeProject.style }}</dd></div>
              <div><dt>空间面积</dt><dd>{{ activeProject.area }}㎡</dd></div>
              <div><dt>预计交付</dt><dd>{{ activeProject.deadline }}</dd></div>
              <div><dt>负责人</dt><dd>杨设计</dd></div>
            </dl>
          </section>

          <section>
            <span>最近沟通</span>
            <strong class="communication-title">{{ activeProject.lastContact.title }}</strong>
            <p>{{ activeProject.lastContact.content }}</p>
            <small>{{ activeProject.lastContact.date }}</small>
          </section>
        </aside>
      </div>
    </template>

    <el-dialog
      title="新建项目"
      :visible.sync="projectDialogVisible"
      custom-class="simple-project-dialog"
      width="520px"
      append-to-body
    >
      <div class="project-form">
        <label>
          <span>归属客户</span>
          <el-select v-model="projectDraft.clientId" placeholder="请选择客户">
            <el-option
              v-for="client in clients"
              :key="client.id"
              :label="client.name"
              :value="client.id"
            />
          </el-select>
        </label>
        <label>
          <span>小区 / 空间名称</span>
          <input v-model.trim="projectDraft.community" placeholder="例如：滨江云境" />
        </label>
        <label>
          <span>门牌 / 项目说明</span>
          <input v-model.trim="projectDraft.unit" placeholder="例如：8-1-1602 或 展厅升级" />
        </label>
        <div>
          <label><span>项目类型</span><input v-model.trim="projectDraft.type" /></label>
          <label><span>面积（㎡）</span><input v-model.number="projectDraft.area" type="number" /></label>
        </div>
        <label>
          <span>设计风格</span>
          <input v-model.trim="projectDraft.style" placeholder="例如：现代简约" />
        </label>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="projectDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="createProject">创建项目</el-button>
      </span>
    </el-dialog>
  </section>
</template>

<script>
const clients = [
  {
    id: 'client-hu',
    name: '胡总',
    phone: '138 6688 2016',
    source: '老客户转介绍',
    color: '#285ea8',
    projects: [
      {
        id: 'project-hu-home',
        code: 'YZ-2026-071',
        name: '滨江云境 8-1-1602',
        community: '滨江云境',
        unit: '8-1-1602',
        type: '住宅全案',
        area: 168,
        style: '现代简约',
        deadline: '2026-08-18',
        status: 'active',
        stageIndex: 2,
        nextAction: '确认客厅方向',
        requirement: '一家四口居住，希望公共空间开阔、储物充足；整体克制耐看，重点解决客餐厅采光和儿童房成长性。',
        cover: '/api/files/133dc4ff-6284-4466-a16b-c4d2871f1857.png',
        images: [
          '/api/files/133dc4ff-6284-4466-a16b-c4d2871f1857.png',
          '/api/files/27c18903-5980-4eef-a4c8-0b61eb02336e.png',
          '/api/files/10bec750-2876-44a6-ac39-0462605d9a21.png'
        ],
        tasks: [
          { id: 'hu-task-1', title: '确认客厅电视墙方向', date: '今天', done: false },
          { id: 'hu-task-2', title: '补充儿童房收纳方案', date: '7月12日', done: false },
          { id: 'hu-task-3', title: '核对现场量房尺寸', date: '已完成', done: true }
        ],
        lastContact: {
          title: '第一版方向沟通',
          content: '胡总认可整体配色，希望减少电视墙石材比例，儿童房不要做得太低龄。',
          date: '今天 10:30'
        }
      },
      {
        id: 'project-hu-office',
        code: 'YZ-2026-058',
        name: '启明建材展厅升级',
        community: '启明建材展厅',
        unit: '展厅升级',
        type: '商业空间',
        area: 320,
        style: '现代轻奢',
        deadline: '2026-09-10',
        status: 'paused',
        stageIndex: 1,
        nextAction: '等待产品清单',
        requirement: '强调瓷砖、岩板和定制柜的真实展示效果，让洽谈区更有品质感，销售动线更清晰。',
        cover: '/api/files/345bb2c6-7fa1-4a84-a946-3674ab88c336.png',
        images: [
          '/api/files/345bb2c6-7fa1-4a84-a946-3674ab88c336.png',
          '/api/files/3ff80890-8a3d-46ec-98aa-5128b3de6bb8.png'
        ],
        tasks: [
          { id: 'office-task-1', title: '等待甲方提供最新产品清单', date: '7月15日', done: false }
        ],
        lastContact: {
          title: '展厅改造初谈',
          content: '先等新一季主推产品清单，再完成展示模块和参观动线规划。',
          date: '6月28日'
        }
      }
    ]
  },
  {
    id: 'client-lin',
    name: '林女士',
    phone: '186 5210 7732',
    source: '小红书咨询',
    color: '#9a4e57',
    projects: [
      {
        id: 'project-lin-home',
        code: 'YZ-2026-076',
        name: '江南里 3-2-901',
        community: '江南里',
        unit: '3-2-901',
        type: '住宅软装',
        area: 112,
        style: '奶油原木',
        deadline: '2026-07-30',
        status: 'active',
        stageIndex: 3,
        nextAction: '确认沙发面料',
        requirement: '精装房软装改造，保留地板和大部分硬装，通过家具、灯具、窗帘与局部墙面调整改善家的温度。',
        cover: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&w=1200&q=86',
        images: [
          'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&w=1200&q=86',
          'https://images.unsplash.com/photo-1618220179428-22790b461013?auto=format&fit=crop&w=1200&q=86'
        ],
        tasks: [
          { id: 'lin-task-1', title: '输出家具尺寸清单', date: '7月11日', done: false },
          { id: 'lin-task-2', title: '确认主卧窗帘面料', date: '7月12日', done: false }
        ],
        lastContact: {
          title: '第三版方案确认',
          content: '整体方案通过，沙发颜色需要看实体面料后最终确认。',
          date: '昨天 18:20'
        }
      }
    ]
  },
  {
    id: 'client-zhou',
    name: '周总',
    phone: '139 1718 6629',
    source: '合作方推荐',
    color: '#3e7667',
    projects: [
      {
        id: 'project-zhou-office',
        code: 'YZ-2026-043',
        name: '澄川科技办公总部',
        community: '澄川科技',
        unit: '办公总部',
        type: '办公空间',
        area: 860,
        style: '科技自然',
        deadline: '2026-07-26',
        status: 'completed',
        stageIndex: 5,
        nextAction: '项目归档',
        requirement: '兼顾对外品牌展示和内部高效协作，减少传统封闭办公室，增加灵活会议与安静专注空间。',
        cover: 'https://images.unsplash.com/photo-1497366811353-6870744d04b2?auto=format&fit=crop&w=1200&q=86',
        images: [
          'https://images.unsplash.com/photo-1497366811353-6870744d04b2?auto=format&fit=crop&w=1200&q=86',
          'https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=1200&q=86'
        ],
        tasks: [
          { id: 'zhou-task-1', title: '整理最终交付资料', date: '已完成', done: true }
        ],
        lastContact: {
          title: '最终交付确认',
          content: '施工图和效果图资料已经交付，客户确认无遗漏。',
          date: '7月8日'
        }
      }
    ]
  }
]

export default {
  name: 'ProjectManagement',
  data() {
    return {
      view: 'list',
      keyword: '',
      activeFilter: 'all',
      activeClient: null,
      activeProject: null,
      projectDialogVisible: false,
      clients,
      stages: ['需求沟通', '现场量房', '概念方案', '深化设计', '报价确认', '项目交付'],
      filters: [
        { label: '全部', value: 'all' },
        { label: '进行中', value: 'active' },
        { label: '暂缓', value: 'paused' },
        { label: '已完成', value: 'completed' }
      ],
      projectDraft: {
        clientId: 'client-hu',
        community: '',
        unit: '',
        type: '住宅全案',
        area: 120,
        style: '现代简约'
      }
    }
  },
  computed: {
    allProjects() {
      return this.clients.reduce((all, client) => [...all, ...client.projects], [])
    },
    activeProjectCount() {
      return this.allProjects.filter(project => project.status === 'active').length
    },
    pendingProjectCount() {
      return this.allProjects.filter(project => project.status === 'paused').length
    },
    completedProjectCount() {
      return this.allProjects.filter(project => project.status === 'completed').length
    },
    filteredClients() {
      return this.clients.filter(client => this.visibleProjects(client).length)
    }
  },
  methods: {
    visibleProjects(client) {
      const keyword = this.keyword.toLowerCase()
      return client.projects.filter(project => {
        const matchesKeyword = !keyword || [
          client.name,
          client.phone,
          project.name,
          project.community,
          project.unit,
          project.type,
          project.style
        ].join(' ').toLowerCase().includes(keyword)
        const matchesStatus = this.activeFilter === 'all' || project.status === this.activeFilter
        return matchesKeyword && matchesStatus
      })
    },
    openProject(client, project) {
      this.activeClient = client
      this.activeProject = project
      this.view = 'detail'
      this.$nextTick(() => window.scrollTo({ top: 0, behavior: 'smooth' }))
    },
    backToList() {
      this.view = 'list'
      this.activeClient = null
      this.activeProject = null
      this.$nextTick(() => window.scrollTo({ top: 0, behavior: 'smooth' }))
    },
    stageName(index) {
      return this.stages[index] || this.stages[0]
    },
    statusText(status) {
      return { active: '进行中', paused: '暂缓', completed: '已完成' }[status] || status
    },
    projectIdentifier(client, project) {
      return `${project.community || project.name} · ${client.name} · ${project.area}㎡`
    },
    resetFilters() {
      this.keyword = ''
      this.activeFilter = 'all'
    },
    openProjectDialog() {
      this.projectDialogVisible = true
    },
    createProject() {
      const client = this.clients.find(item => item.id === this.projectDraft.clientId)
      if (!client || !this.projectDraft.community) {
        this.$message.warning('请选择客户并填写小区或空间名称')
        return
      }
      const projectName = [this.projectDraft.community, this.projectDraft.unit]
        .filter(Boolean)
        .join(' ')
      const project = {
        id: `project-${Date.now()}`,
        code: `YZ-2026-${String(this.allProjects.length + 80).padStart(3, '0')}`,
        name: projectName,
        community: this.projectDraft.community,
        unit: this.projectDraft.unit,
        type: this.projectDraft.type,
        area: Number(this.projectDraft.area || 0),
        style: this.projectDraft.style,
        deadline: '待确定',
        status: 'active',
        stageIndex: 0,
        nextAction: '完善客户需求',
        requirement: '暂未记录客户核心诉求。',
        cover: 'https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?auto=format&fit=crop&w=1200&q=86',
        images: [],
        tasks: [
          { id: `task-${Date.now()}`, title: '完成首次需求沟通', date: '本周', done: false }
        ],
        lastContact: {
          title: '新项目已建立',
          content: '等待补充第一次客户沟通记录。',
          date: '刚刚'
        }
      }
      client.projects.unshift(project)
      this.projectDialogVisible = false
      this.projectDraft = {
        clientId: client.id,
        community: '',
        unit: '',
        type: '住宅全案',
        area: 120,
        style: '现代简约'
      }
      this.$message.success('项目已创建')
      this.openProject(client, project)
    },
    advanceStage() {
      if (this.activeProject.stageIndex >= this.stages.length - 1) {
        this.$message.success('项目已到交付阶段')
        return
      }
      this.activeProject.stageIndex += 1
      if (this.activeProject.stageIndex === this.stages.length - 1) {
        this.activeProject.status = 'completed'
      }
      this.$message.success(`已推进至“${this.stageName(this.activeProject.stageIndex)}”`)
    }
  }
}
</script>

<style scoped>
.project-management {
  width: calc(100% - 68px);
  max-width: none;
  margin: 28px auto 58px;
}

.page-heading,
.detail-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 20px;
}

.page-heading > div > span,
.detail-heading > div > span {
  display: block;
  margin-bottom: 6px;
  color: #2563eb;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.5px;
}

.page-heading h2,
.detail-heading h2 {
  margin: 0;
  color: #1e2a23;
  font-size: 25px;
  letter-spacing: 0;
}

.page-heading p,
.detail-heading p {
  margin: 7px 0 0;
  color: #7c8780;
  font-size: 11px;
}

.primary-button,
.secondary-button {
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border-radius: 5px;
  padding: 0 14px;
  font-size: 11px;
  font-weight: 600;
}

.primary-button {
  border: 1px solid #2563eb;
  background: #2563eb;
  color: #fff;
  box-shadow: 0 6px 14px rgba(37, 99, 235, .18);
}

.secondary-button {
  border: 1px solid #dbe2dc;
  background: #fff;
  color: #56635b;
}

.project-overview {
  display: flex;
  align-items: center;
  gap: 0;
  margin-bottom: 14px;
  border: 1px solid #e0e5e1;
  background: #fff;
}

.project-overview div {
  min-width: 150px;
  display: flex;
  align-items: baseline;
  gap: 7px;
  padding: 14px 18px;
}

.project-overview div + div { border-left: 1px solid #e6eae7; }
.project-overview strong { color: #29362e; font-size: 20px; }
.project-overview span { color: #8c9690; font-size: 9px; }

.project-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 13px 15px;
  border: 1px solid #e0e5e1;
  background: #fff;
}

.project-toolbar > label {
  width: min(460px, 52%);
  height: 38px;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #dce2dd;
  border-radius: 4px;
  padding: 0 10px;
  background: #fafbfa;
  color: #8d9791;
}

.project-toolbar input {
  width: 100%;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: #35423a;
  font-size: 10px;
}

.project-toolbar label button {
  width: 22px;
  height: 22px;
  border: 0;
  background: transparent;
  color: #929b95;
}

.project-toolbar > div {
  display: flex;
  gap: 3px;
  padding: 3px;
  border-radius: 4px;
  background: #edf0ee;
}

.project-toolbar > div button {
  height: 30px;
  border: 0;
  border-radius: 3px;
  padding: 0 12px;
  background: transparent;
  color: #7d8781;
  font-size: 9px;
}

.project-toolbar > div button.active {
  background: #fff;
  color: #2258bb;
  box-shadow: 0 2px 5px rgba(38, 50, 43, .08);
}

.client-groups {
  display: grid;
  gap: 18px;
  margin-top: 18px;
}

.client-group {
  border: 1px solid #e0e5e1;
  background: #fff;
}

.client-heading {
  min-height: 66px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 15px;
  border-bottom: 1px solid #e7ebe8;
}

.client-avatar {
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
}

.client-avatar.large {
  width: 42px;
  height: 42px;
  flex-basis: 42px;
  font-size: 14px;
}

.client-heading > div {
  min-width: 0;
  flex: 1;
}

.client-heading h3 {
  margin: 0;
  color: #344139;
  font-size: 13px;
}

.client-heading p {
  margin: 4px 0 0;
  color: #949d97;
  font-size: 8px;
}

.client-heading > span:last-child {
  color: #8a948e;
  font-size: 9px;
}

.project-list {
  display: grid;
}

.project-row {
  width: 100%;
  min-width: 0;
  min-height: 92px;
  display: grid;
  grid-template-columns: 112px minmax(220px, 1.5fr) minmax(100px, .6fr) minmax(140px, .8fr) 22px;
  align-items: center;
  gap: 16px;
  border: 0;
  border-bottom: 1px solid #edf0ee;
  padding: 11px 15px;
  background: #fff;
  text-align: left;
  transition: background .18s ease;
}

.project-row:last-child { border-bottom: 0; }
.project-row:hover { background: #f8faf8; }

.project-row > img {
  width: 112px;
  height: 68px;
  display: block;
  border-radius: 4px;
  object-fit: cover;
  background: #e5eae6;
}

.project-main,
.project-stage,
.project-next {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.project-title-line {
  display: flex;
  align-items: center;
  gap: 8px;
}

.project-title-line strong,
.project-stage strong,
.project-next strong {
  overflow: hidden;
  color: #35423a;
  font-size: 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.project-title-line strong {
  font-size: 12px;
  line-height: 1.35;
  white-space: normal;
}
.project-main > small,
.project-stage small,
.project-next small { color: #939d96; font-size: 8px; }

.project-location {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #647169 !important;
  font-size: 9px !important;
}

.project-location i {
  flex: 0 0 auto;
  color: #7390c9;
}

.project-title-line em {
  flex: 0 0 auto;
  border-radius: 3px;
  padding: 3px 6px;
  font-size: 7px;
  font-style: normal;
}

.status-active { background: #eaf2ff; color: #245cc0; }
.status-paused { background: #fff3dc; color: #a56a0d; }
.status-completed { background: #eaf7ee; color: #278851; }
.project-row > i { color: #a3aba6; }

.empty-state {
  min-height: 330px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 9px;
  margin-top: 18px;
  border: 1px dashed #d7ddd8;
  background: #fff;
  color: #8e9892;
}

.empty-state > i { font-size: 30px; color: #86a4df; }
.empty-state strong { color: #536058; font-size: 12px; }
.empty-state button { border: 0; background: transparent; color: #2563eb; font-size: 9px; }

.back-button {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 18px;
  border: 0;
  padding: 0;
  background: transparent;
  color: #6e7a72;
  font-size: 10px;
}

.detail-heading {
  align-items: center;
  margin-bottom: 16px;
}

.detail-heading > div:last-child {
  display: flex;
  gap: 8px;
}

.detail-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 280px;
  gap: 14px;
}

.detail-main {
  min-width: 0;
  display: grid;
  gap: 14px;
}

.project-cover {
  min-height: 270px;
  display: grid;
  grid-template-columns: minmax(300px, 1.2fr) minmax(280px, .8fr);
  border: 1px solid #e0e5e1;
  background: #fff;
}

.project-cover > img {
  width: 100%;
  height: 100%;
  min-height: 270px;
  display: block;
  object-fit: cover;
  background: #e5eae6;
}

.project-cover > div {
  display: flex;
  justify-content: center;
  flex-direction: column;
  padding: 26px;
}

.project-cover span,
.detail-aside > section > span {
  color: #8c9690;
  font-size: 8px;
  font-weight: 600;
}

.project-cover h3 {
  margin: 8px 0 10px;
  color: #2c3931;
  font-size: 19px;
}

.project-cover p,
.detail-aside p {
  margin: 0;
  color: #69756d;
  font-size: 10px;
  line-height: 1.75;
}

.detail-section {
  border: 1px solid #e0e5e1;
  padding: 17px;
  background: #fff;
}

.section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}

.section-heading > div {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.section-heading strong {
  color: #334038;
  font-size: 12px;
}

.section-heading span {
  color: #939d96;
  font-size: 8px;
}

.section-heading > b { color: #2563eb; font-size: 13px; }
.section-heading > button { border: 0; background: transparent; color: #2563eb; font-size: 9px; }

.stage-track {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  margin-top: 22px;
}

.stage-track > div {
  min-width: 0;
  position: relative;
  text-align: center;
}

.stage-track > div::before,
.stage-track > div::after {
  content: "";
  height: 2px;
  position: absolute;
  top: 9px;
  background: #dfe5e0;
}

.stage-track > div::before { right: 50%; left: 0; }
.stage-track > div::after { right: 0; left: 50%; }
.stage-track > div:first-child::before,
.stage-track > div:last-child::after { display: none; }
.stage-track > div.completed::before,
.stage-track > div.completed::after,
.stage-track > div.current::before { background: #4f7fda; }

.stage-track > div > span {
  width: 19px;
  height: 19px;
  position: relative;
  z-index: 1;
  display: grid;
  place-items: center;
  margin: 0 auto 8px;
  border: 2px solid #d9e0db;
  border-radius: 50%;
  background: #fff;
  color: #fff;
  font-size: 8px;
}

.stage-track > div.completed > span {
  border-color: #4f7fda;
  background: #4f7fda;
}

.stage-track > div.current > span {
  border-color: #2563eb;
  box-shadow: 0 0 0 4px #eaf2ff;
}

.stage-track small {
  color: #8a958e;
  font-size: 8px;
  white-space: nowrap;
}

.stage-track .completed small,
.stage-track .current small { color: #2d61c2; }

.scheme-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-top: 15px;
}

.scheme-grid button {
  min-width: 0;
  position: relative;
  overflow: hidden;
  border: 0;
  border-radius: 4px;
  padding: 0;
  background: #e6ebe7;
  aspect-ratio: 16 / 10;
}

.scheme-grid img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.scheme-grid button span {
  position: absolute;
  left: 8px;
  bottom: 8px;
  border-radius: 3px;
  padding: 4px 6px;
  background: rgba(24, 34, 28, .72);
  color: #fff;
  font-size: 7px;
}

.next-step-section {
  padding-bottom: 8px;
}

.next-step-section label {
  min-height: 48px;
  display: flex;
  align-items: center;
  gap: 9px;
  border-bottom: 1px solid #edf0ee;
  cursor: pointer;
}

.next-step-section label:last-child { border-bottom: 0; }
.next-step-section input { display: none; }

.next-step-section label > span {
  width: 17px;
  height: 17px;
  flex: 0 0 17px;
  display: grid;
  place-items: center;
  border: 1px solid #cbd4cd;
  border-radius: 3px;
  color: transparent;
  font-size: 8px;
}

.next-step-section input:checked + span {
  border-color: #3e75d8;
  background: #3e75d8;
  color: #fff;
}

.next-step-section label > strong {
  flex: 1;
  color: #4a574f;
  font-size: 9px;
}

.next-step-section label > small { color: #949e97; font-size: 8px; }
.next-step-section label.done > strong { color: #9ba49e; text-decoration: line-through; }

.detail-aside {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.detail-aside > section {
  border: 1px solid #e0e5e1;
  padding: 17px;
  background: #fff;
}

.client-card {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 13px 0 10px;
}

.client-card > div {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.client-card strong,
.communication-title { color: #36433b; font-size: 11px; }
.client-card small,
.detail-aside > section > small { color: #959f98; font-size: 8px; }

.detail-aside dl {
  margin: 10px 0 0;
}

.detail-aside dl > div {
  min-height: 36px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border-bottom: 1px solid #edf0ee;
}

.detail-aside dl > div:last-child { border-bottom: 0; }
.detail-aside dt { color: #8d9791; font-size: 8px; }
.detail-aside dd { margin: 0; color: #48554d; font-size: 9px; font-weight: 600; }

.communication-title {
  display: block;
  margin: 12px 0 7px;
}

.detail-aside > section > small {
  display: block;
  margin-top: 10px;
}

.project-form {
  display: grid;
  gap: 14px;
}

.project-form > div {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.project-form label {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.project-form label > span {
  color: #536058;
  font-size: 10px;
  font-weight: 600;
}

.project-form input {
  width: 100%;
  height: 38px;
  border: 1px solid #d9e0da;
  border-radius: 4px;
  outline: 0;
  padding: 0 11px;
  color: #344139;
  font-size: 11px;
}

.project-form input:focus {
  border-color: #7f9fdf;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, .08);
}

.project-form ::v-deep .el-select { width: 100%; }
.project-form ::v-deep .el-input__inner { height: 38px; border-radius: 4px; font-size: 11px; }

@media (max-width: 1000px) {
  .project-row {
    grid-template-columns: 100px minmax(200px, 1fr) minmax(100px, .6fr) 22px;
  }
  .project-next { display: none; }
  .detail-layout { grid-template-columns: 1fr; }
  .detail-aside { display: grid; grid-template-columns: repeat(3, 1fr); }
}

@media (max-width: 767px) {
  .project-management { width: calc(100% - 20px); margin-top: 15px; }
  .page-heading,
  .detail-heading { align-items: stretch; flex-direction: column; gap: 13px; }
  .page-heading h2,
  .detail-heading h2 { font-size: 21px; }
  .page-heading .primary-button { width: 100%; }
  .project-overview div { min-width: 0; flex: 1; justify-content: center; padding: 12px 6px; }
  .project-toolbar { align-items: stretch; flex-direction: column; }
  .project-toolbar > label { width: 100%; }
  .project-toolbar > div { display: grid; grid-template-columns: repeat(4, 1fr); }
  .project-toolbar > div button { padding: 0 4px; }
  .project-row {
    grid-template-columns: 82px minmax(0, 1fr) 18px;
    gap: 10px;
    padding: 10px;
  }
  .project-row > img { width: 82px; height: 62px; }
  .project-stage,
  .project-next { display: none; }
  .project-title-line { align-items: flex-start; flex-direction: column; gap: 5px; }
  .project-title-line strong { overflow: visible; }
  .project-main > small { white-space: normal; line-height: 1.45; }
  .detail-heading > div:last-child { display: grid; grid-template-columns: 1fr 1fr; }
  .project-cover { grid-template-columns: 1fr; }
  .project-cover > img { min-height: 220px; }
  .project-cover > div { padding: 18px; }
  .detail-section { padding: 14px 10px; overflow-x: auto; }
  .stage-track { min-width: 560px; }
  .scheme-grid { grid-template-columns: 1fr; }
  .detail-aside { grid-template-columns: 1fr; }
  .project-form > div { grid-template-columns: 1fr; }
}
</style>
